import { Icon } from '@design/components/icon/Icon';
import { RadioGroup } from '@design/components/radio-group/RadioGroup';
import * as Radix from '@radix-ui/react-dropdown-menu';
import { TruncateTooltip } from '@terminal/components/truncate-tooltip/TruncateTooltip';
import { INDEX_ID, WATCHLIST_ID } from '@terminal/config/constants';
import { usePublicLists } from '@terminal/react-query-hooks/usePublicLists';
import { Watchlist } from '@terminal/stores/use-watchlist/watchlist-types';
import clsx from 'clsx';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AssetsConstituents, SelectedAssetItem } from './RunScenario';
import styles from './RunScenario.module.scss';
import { getPublicListId } from './utils/utils';

const MAX_NAME_LENGTH = 15;

interface AssetsDropdownProps {
  selectedAssets?: SelectedAssetItem;
  watchlistLists: Watchlist[];
  handleSelectAssets: (args: SelectedAssetItem) => void;
}

export const AssetsDropdown = ({
  selectedAssets,
  watchlistLists,
  handleSelectAssets,
}: AssetsDropdownProps) => {
  const { t } = useTranslation(['calendar']);
  const { data: publicLists } = usePublicLists();

  const [isOpen, setIsOpen] = useState(false);

  const watchlistOptions = watchlistLists.map(list => ({
    id: list.id,
    label: <TruncateTooltip text={list.name} atLength={MAX_NAME_LENGTH} />,
  }));
  const indexOptions = [
    {
      id:
        getPublicListId(AssetsConstituents.SPX, publicLists)?.id ||
        AssetsConstituents.SPX,
      /* i18next-extract-mark-context-next-line ["spx", "ftse100", "dji", "ni225"] */
      label: t('calendar:scenario.assetsOption', {
        context: AssetsConstituents.SPX,
      }),
    },
    {
      id:
        getPublicListId(AssetsConstituents.FTSE100, publicLists)?.id ||
        AssetsConstituents.FTSE100,
      /* i18next-extract-mark-context-next-line ["spx", "ftse100", "dji", "ni225"] */
      label: t('calendar:scenario.assetsOption', {
        context: AssetsConstituents.FTSE100,
      }),
    },
    {
      id:
        getPublicListId(AssetsConstituents.DJI, publicLists)?.id ||
        AssetsConstituents.DJI,
      /* i18next-extract-mark-context-next-line ["spx", "ftse100", "dji", "ni225"] */
      label: t('calendar:scenario.assetsOption', {
        context: AssetsConstituents.DJI,
      }),
    },
    {
      id:
        getPublicListId(AssetsConstituents.NI225, publicLists)?.id ||
        AssetsConstituents.NI225,
      /* i18next-extract-mark-context-next-line ["spx", "ftse100", "dji", "ni225"] */
      label: t('calendar:scenario.assetsOption', {
        context: AssetsConstituents.NI225,
      }),
    },
  ];
  const options = [...watchlistOptions, ...indexOptions];

  return (
    <div className={styles.filterWrapper}>
      <label className={styles.filterLabel}>{t('calendar:scenario.whatLabel')}</label>
      <Radix.Root open={isOpen} onOpenChange={setIsOpen}>
        <Radix.Trigger
          className={clsx(styles.trigger, { [styles.active]: !!selectedAssets })}
          onClick={() => setIsOpen(true)}
        >
          <span className={styles.triggerLabel} data-testid="asset-dropdown-label">
            {options.find(option => option.id === selectedAssets?.id)?.label ??
              t('calendar:scenario.addAssets')}
          </span>
          <Icon className={styles.triggerArrow} iconName="ChevronLightDown" size={14} />
        </Radix.Trigger>
        <Radix.Portal>
          <Radix.Content
            className={styles.dropdownMenuContent}
            align="start"
            sideOffset={5}
            collisionPadding={5}
            onInteractOutside={() => setIsOpen(false)}
          >
            {watchlistOptions && (
              <>
                <div className={styles.watchlistGroup}>
                  {t('calendar:scenario.watchlist')}
                </div>
                <RadioGroup
                  className={styles.styledRadioGroup}
                  items={watchlistOptions}
                  activeId={selectedAssets?.id}
                  onClick={(id: string) =>
                    handleSelectAssets({
                      assetFilterValueKey: WATCHLIST_ID,
                      id,
                    })
                  }
                />
                <Radix.Separator className={styles.separator} />
              </>
            )}
            <RadioGroup
              className={styles.styledRadioGroup}
              items={indexOptions}
              activeId={selectedAssets?.id}
              onClick={(id: string) =>
                handleSelectAssets({
                  assetFilterValueKey: INDEX_ID,
                  id,
                })
              }
            />
          </Radix.Content>
        </Radix.Portal>
      </Radix.Root>
    </div>
  );
};
